import React, {RefObject} from 'react';
import {v4} from 'uuid';
import './Chat.scss';
import {
  ChatContainer,
  MainContainer,
  Message,
  MessageInput,
  MessageList,
  MessageModel,
} from '@chatscope/chat-ui-kit-react';
import {TaskInfo} from '../../types/metadata/TaskInfo';

export type MessageType = 'SYSTEM' | 'APPLICATION';

export interface ChatConnection {
  sendText: (s: string) => void;
  sendImage: (s: string) => void;
  setTextConsumer: (x: (m: string, t: MessageType) => void) => void;
  setImageConsumer: (x: (m: string) => void) => void;
  setStatusConsumer: (x: (s: ChatConnectionStatus) => void) => void;
  updateTaskInfo: (x: TaskInfo) => void;
  close: () => void;
}

export enum ChatConnectionStatus {
  CONNECTING,
  CONNECTED,
  ACTIVE,
  DISCONNECTED,
  ERROR,
}

export type ChatProps = {
  connection: ChatConnection;
};

interface MessageModelWithId extends MessageModel {
  id: string;
}

type State = {
  connection: ChatConnection;
  status: ChatConnectionStatus;
  history: MessageModelWithId[];
  messageInputValue: string;
  messageText: string;
  inputRef: RefObject<HTMLInputElement>;
};

export class Chat extends React.Component<ChatProps, State> {
  constructor(props: ChatProps, context: any) {
    super(props, context);
    const self = this;
    this.props.connection.setTextConsumer((m, t) => this.onText(m, t, self));
    this.props.connection.setImageConsumer(m => this.onImage(m, self));
    this.props.connection.setStatusConsumer(x => self.setState({status: x}));
    this.state = {
      connection: this.props.connection,
      status: ChatConnectionStatus.CONNECTING,
      history: [],
      messageInputValue: '',
      messageText: '',
      inputRef: React.createRef(),
    };
  }

  componentWillUnmount() {
    this.props.connection.close();
  }

  private onText(s: String, t: MessageType, self: Chat) {
    const date = new Date();
    const inMsg = {
      id: v4(),
      message: t === 'SYSTEM' ? '<i>' + s + '</i>' : s,
      sentTime: date.toDateString() + ' ' + date.toTimeString(),
      sender: 'Application',
      direction: 'incoming',
      position: 0,
    } as MessageModelWithId;
    self.setState({history: [...self.state.history, inMsg]});
    return;
  }

  private onImage(s: String, self: Chat) {
    const date = new Date();
    const inMsg = {
      id: v4(),
      type: 'html', // TODO: Could not make type 'image' work with payload and Message.ImageContent
      message: `<img src="${s}" alt="incoming image" width="100%"/>`,
      sentTime: date.toDateString() + ' ' + date.toTimeString(),
      sender: 'Application',
      direction: 'incoming',
      position: 0,
    } as MessageModelWithId;
    self.setState({history: [...self.state.history, inMsg]});
    return;
  }

  loading(): boolean {
    return (
      this.state.status === ChatConnectionStatus.CONNECTING ||
      this.state.status === ChatConnectionStatus.CONNECTED
    );
  }

  disabled(): boolean {
    return (
      this.state.status === ChatConnectionStatus.DISCONNECTED ||
      this.state.status === ChatConnectionStatus.ERROR
    );
  }

  render() {
    return (
      <div style={{position: 'relative', width: '100%'}}>
        <input
          type="file"
          style={{display: 'none'}}
          ref={this.state.inputRef}
          onChange={onChangeEvent => {
            const file = onChangeEvent.target?.files?.[0] as any;
            if (!file) {
              return;
            }
            const fileReader = new FileReader();
            const chat = this;
            fileReader.onload = onloadEvent => {
              const imageBase64 = onloadEvent.target?.result as string;
              const date = new Date();
              const outMsg = {
                id: v4(),
                type: 'html', // TODO: Could not make type 'image' work with payload and Message.ImageContent
                message: `<img src="${imageBase64}" alt="${file.name}" width="100%"/>`,
                sentTime: date.toDateString() + ' ' + date.toTimeString(),
                sender: 'Me',
                direction: 'outgoing',
                position: 0,
              } as MessageModelWithId;
              chat.setState({history: [...chat.state.history, outMsg]});
              chat.state.connection.sendImage(imageBase64);
            };
            fileReader.readAsDataURL(file);
          }}
        />
        {/*<MainContainer style={styles.csMainContainer}>*/}
        <MainContainer>
          <ChatContainer>
            <MessageList loading={this.loading()}>
              {this.state.history.map(x => (
                <Message model={x} key={x.id}>
                  <Message.Footer sentTime={x.sentTime} />
                </Message>
              ))}
            </MessageList>
            <MessageInput
              attachButton={true}
              onAttachClick={e => {
                this.state.inputRef.current?.click();
              }}
              value={this.disabled() ? 'Not connected' : this.state.messageInputValue}
              sendButton={!this.disabled()}
              onChange={(ih, tc, it, n) =>
                this.setState({
                  messageInputValue: ih,
                  messageText: it,
                })
              }
              disabled={this.loading() || this.disabled()}
              onSend={() => {
                const message = this.state.messageText;
                this.setState({messageInputValue: '', messageText: ''});
                const date = new Date();
                const outMsg = {
                  id: v4(),
                  message: message,
                  sentTime: date.toDateString() + ' ' + date.toTimeString(),
                  sender: 'Me',
                  direction: 'outgoing',
                  position: 0,
                } as MessageModelWithId;
                this.setState({history: [...this.state.history, outMsg]});
                this.state.connection.sendText(message);
              }}
            />
          </ChatContainer>
        </MainContainer>
      </div>
    );
  }
}
